import clsx from 'clsx';
import { isPlainObject } from 'lodash';
import { FunctionComponent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AdminQueryUser } from '../../../api/admin';
import IconNewTab from '../../../components/icons/IconNewTab';
import {
  ADMIN_DATA_FORMAT_MAPPERS,
  ADMIN_DATA_LINK_MAPPERS,
  ADMIN_DATA_URL_MAPPERS,
  getAdminCognitoUrl,
  getAdminDynamoOrgUrl,
  getAdminDynamoOrgUsersUrl,
  getAdminDynamoUserUrl,
  getAdminStripeCustomerUrl,
} from '../utils';
import AdminActionsForUser from './AdminActionsForUser';

type Namespace = keyof AdminQueryUser;

type SubGroups = Partial<Record<string, string[]>>;

const DBUSER_SUB_GROUPS: SubGroups = {
  user: ['userId', 'type', 'created', 'updated', 'is_staff', 'accept_language'],
  org: ['orgId', 'orgRole', 'orgStatus'],
  cognito: ['email', 'sub'],
  emailLog: ['emailLog'],
  stripe: [
    'customerId',
    'planId',
    'subscriptionId',
    'status',
    'cancel_at_period_end',
    'amount',
    'interval',
    'interval_count',
    'tax_percent',
    'current_period_start',
    'current_period_end',
    'trial_end',
    'postal_code',
  ],
};

const DBORG_USB_GROUPS: SubGroups = {
  org: ['orgId', 'type', 'name', 'adminEmail', 'seats', 'created', 'updated'],
  stripe: DBUSER_SUB_GROUPS.stripe,
};

type AdminUserProps = {
  className?: string;
  user: AdminQueryUser;
  requeryUser: () => Promise<void>;
};

const AdminUser: FunctionComponent<AdminUserProps> = ({
  className,
  user,
  requeryUser,
}) => {
  return (
    <div className={clsx(className, 'space-y-6')}>
      <AdminActionsForUser user={user} requeryUser={requeryUser} />
      <Group
        title="Cognito"
        namespace="cognito"
        items={user.cognito}
        url={
          user.cognito?.attrs.sub
            ? getAdminCognitoUrl(user.cognito.attrs.sub)
            : undefined
        }
      />
      <Group
        title="Dynamo (User)"
        namespace="dbUser"
        items={user.dbUser}
        url={
          user.dbUser?.userId
            ? getAdminDynamoUserUrl(user.dbUser.userId)
            : undefined
        }
        subGroups={DBUSER_SUB_GROUPS}
      />
      <Group
        title="Dynamo (Org)"
        namespace="dbOrg"
        items={user.dbOrg}
        url={
          user.dbOrg?.orgId ? getAdminDynamoOrgUrl(user.dbOrg.orgId) : undefined
        }
        extraUrl={
          user.dbOrg
            ? {
                title: 'users',
                url: getAdminDynamoOrgUsersUrl(user.dbOrg.orgId),
              }
            : undefined
        }
        subGroups={DBORG_USB_GROUPS}
      />
      <Group
        title="Stripe"
        namespace="stripeCustomer"
        // TODO - types fix
        items={user.stripeCustomer as unknown as Items}
        url={
          user.stripeCustomer?.id
            ? getAdminStripeCustomerUrl(user.stripeCustomer.id)
            : undefined
        }
      />
      {/* <div className="whitespace-pre-wrap">{JSON.stringify(user, null, 2)}</div> */}
    </div>
  );
};

AdminUser.displayName = 'AdminUser';

export default AdminUser;

// ## Helpers

type Items = Record<string, unknown>;

type GroupProps = {
  namespace: Namespace;
  title: string;
  items?: Items;
  url?: string;
  extraUrl?: {
    title: string;
    url: string;
  };
  subGroups?: SubGroups;
};

const Group: FunctionComponent<GroupProps> = ({
  namespace,
  title,
  items,
  url,
  extraUrl,
  subGroups,
}) => {
  if (!items) {
    return null;
  }

  let children: ReactNode;

  if (subGroups) {
    const subChildren: ReactNode[] = [];
    const foundKeys = new Set();
    Object.entries(subGroups).forEach(([group, keys]) => {
      let hasSubItems = false;
      const subItems: Items = {};
      keys?.forEach((k) => {
        if (k in items) {
          foundKeys.add(k);
          hasSubItems = true;
          subItems[k] = items[k];
        }
      });
      if (hasSubItems) {
        subChildren.push(
          <div key={group}>
            <div className="text-xs italic text-gray-400">{group}</div>
            <Rows className="pl-4" namespace={namespace} items={subItems} />
          </div>,
        );
      }
    });

    const subItems: Items = {};
    let hasMisc = false;

    Object.entries(items).forEach(([key, value]) => {
      if (!foundKeys.has(key)) {
        subItems[key] = value;
        hasMisc = true;
      }
    });

    if (hasMisc) {
      subChildren.push(
        <div key="__misc__">
          <div className="text-xs italic text-gray-400">misc</div>
          <Rows className="pl-4" namespace={namespace} items={subItems} />
        </div>,
      );
    }

    children = subChildren;
  } else {
    children = <Rows namespace={namespace} items={items} />;
  }

  return (
    <div className="space-y-2 rounded-lg bg-gray-100 p-5 pt-0">
      <div className="sticky top-0 border-0 border-b border-solid border-gray-300 bg-gray-100 pb-2 pt-5 text-sm font-medium text-gray-900">
        {title}
        {url ? (
          <>
            {' '}
            <a
              href={url}
              target="_blank"
              className="text-blue-600 hover:text-blue-800 focus:text-blue-800"
              rel="noreferrer"
            >
              <IconNewTab className="mt-px inline-block h-4 w-4 align-top" />
              <span className="sr-only">Open in new tab</span>
            </a>
          </>
        ) : null}
        {extraUrl ? (
          <>
            {' '}
            (
            <a
              href={extraUrl.url}
              target="_blank"
              className="text-blue-600 hover:text-blue-800 focus:text-blue-800"
              rel="noreferrer"
            >
              {extraUrl.title}{' '}
              <IconNewTab className="mt-px inline-block h-4 w-4 align-top" />
            </a>
            )
          </>
        ) : null}
      </div>
      {children}
    </div>
  );
};

Group.displayName = 'Group';

//

type ItemsProps = {
  namespace: Namespace;
  path?: string;
  className?: string;
  items: Items;
};

const Rows: FunctionComponent<ItemsProps> = ({
  namespace,
  path = '',
  className,
  items,
}) => {
  const rows = items
    ? Object.entries(items).map(([k, v]) => {
        return (
          <Row key={k} namespace={namespace} path={path} name={k} value={v} />
        );
      })
    : null;
  return <div className={clsx('', className)}>{rows}</div>;
};

Rows.displayName = 'Items';

//

type RowProps = {
  namespace: Namespace;
  path: string;
  name: string;
  value: unknown;
};

const Row: FunctionComponent<RowProps> = ({ namespace, path, name, value }) => {
  return (
    <div className="py-1 text-xs">
      <span className="pr-2 text-gray-600">{name}:</span>
      <Value
        namespace={namespace}
        path={_addToPath(path, name)}
        value={value}
      />
    </div>
  );
};

Row.displayName = 'Row';

//

type ValueProps = {
  namespace: keyof AdminQueryUser;
  noPad?: boolean;
  path: string;
  value: unknown;
};

const Value: FunctionComponent<ValueProps> = ({
  namespace,
  noPad,
  path,
  value,
}) => {
  const sValue = String(value);

  const urlMapper = ADMIN_DATA_URL_MAPPERS[namespace][path];
  const url = (sValue && urlMapper?.(sValue)) || undefined;

  const linkMapper = ADMIN_DATA_LINK_MAPPERS[namespace][path];
  const toLink = (sValue && linkMapper?.(sValue)) || undefined;

  const formatter = ADMIN_DATA_FORMAT_MAPPERS[namespace][path];
  const fValue = formatter ? formatter(value) : String(value);

  return isPlainObject(value) ? (
    <Rows
      className={noPad ? undefined : 'pl-4'}
      namespace={namespace}
      path={path}
      items={value as Items}
    />
  ) : Array.isArray(value) ? (
    <ul className="ml-8 list-disc">
      {value.map((v, i) => (
        <li key={i}>
          <Value noPad namespace={namespace} path={`${path}[]`} value={v} />
        </li>
      ))}
    </ul>
  ) : (
    <span className="text-gray-900">
      {toLink ? (
        <Link
          className="text-blue-600 hover:text-blue-800 focus:text-blue-800"
          to={toLink}
        >
          {fValue}
        </Link>
      ) : (
        fValue
      )}
      {url ? (
        <>
          {' '}
          <a
            href={url}
            target="_blank"
            className="text-blue-600 hover:text-blue-800 focus:text-blue-800"
            rel="noreferrer"
          >
            <IconNewTab className="mt-px inline-block h-3 w-3 align-top" />
            <span className="sr-only">new tab</span>
          </a>
        </>
      ) : null}
    </span>
  );
};

Value.displayName = 'Value';

//

const _addToPath = (path: string | undefined, name: string) => {
  return path ? `${path}.${name}` : name;
};
